import { useState, useEffect, useCallback, useRef } from 'react';
import { useAccounts } from '../../../hooks/useAccounts';
import { useCards } from '../../../hooks/useCards';
import { usePayees } from '../../../hooks/usePayees';
import { useCategorySuggestion } from '../../../hooks/useCategorySuggestion';
import { CategoryPicker } from './CategoryPicker';
import { CurrencyInput } from '../../../components/base/CurrencyInput';
import { PAYMENT_METHODS } from './PaymentDetailModal';
import { PayeeSelector } from '../../favorecidos/components/PayeeSelector';
import { trackEvent } from '../../../lib/analytics';
import type { Transaction, TransactionPayload } from '../../../hooks/useTransactions';

interface Props {
  open: boolean;
  transaction?: Transaction | null;
  onClose: () => void;
  onSave: (values: TransactionPayload) => Promise<{ success: boolean; message: string }>;
  onSaveMany?: (records: TransactionPayload[]) => Promise<{ success: boolean; message: string }>;
}

type StatusOption = { value: string; label: string; activeClass: string };

const RECEIPT_STATUSES: StatusOption[] = [
  { value: 'recebido', label: 'Recebido', activeClass: 'border-emerald-400 bg-emerald-50 text-emerald-700' },
  { value: 'pendente', label: 'Pendente', activeClass: 'border-amber-400 bg-amber-50 text-amber-700' },
  { value: 'atrasado', label: 'Atrasado', activeClass: 'border-red-400 bg-red-50 text-red-700' },
];
const EXPENSE_STATUSES: StatusOption[] = [
  { value: 'pago', label: 'Pago', activeClass: 'border-emerald-400 bg-emerald-50 text-emerald-700' },
  { value: 'pendente', label: 'Pendente', activeClass: 'border-amber-400 bg-amber-50 text-amber-700' },
  { value: 'vencido', label: 'Vencido', activeClass: 'border-red-400 bg-red-50 text-red-700' },
];

const RECURRENCES = [
  { value: 'none', label: 'Não se repete', icon: 'ri-close-circle-line' },
  { value: 'weekly', label: 'Semanal', icon: 'ri-calendar-line' },
  { value: 'monthly', label: 'Mensal', icon: 'ri-calendar-2-line' },
  { value: 'annual', label: 'Anual', icon: 'ri-calendar-event-line' },
];

const RECURRENCE_TYPES = [
  { value: 'common', label: 'Recorrente', icon: 'ri-repeat-line', desc: 'Despesa ou receita que se repete regularmente' },
  { value: 'installment', label: 'Parcela', icon: 'ri-bill-line', desc: 'Compra parcelada no cartão ou boleto' },
  { value: 'loan', label: 'Empréstimo', icon: 'ri-hand-coin-line', desc: 'Pagamento de empréstimo pessoal' },
  { value: 'financing', label: 'Financiamento', icon: 'ri-home-5-line', desc: 'Financiamento imobiliário ou de veículo' },
];

const DUE_DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

type RecurrenceEndType = 'none' | 'date' | 'count';
type RecurrenceClassification = 'common' | 'installment' | 'loan' | 'financing';
type AutoFillSource = 'payee' | 'history' | null;

const buildInitialForm = (t?: Transaction | null): TransactionPayload => ({
  type: t?.type ?? 'despesa',
  description: t?.description ?? '',
  category: t?.category ?? '',
  subcategory: t?.subcategory ?? '',
  amount: t?.amount ?? ('' as unknown as number),
  date: t?.date ?? new Date().toISOString().split('T')[0],
  paid_at: t?.paid_at ?? (
    (t?.status === 'pago' || t?.status === 'recebido')
      ? (t?.date ?? new Date().toISOString().split('T')[0])
      : null
  ),
  payment_method: (t?.payment_method as 'account' | 'card') ?? 'account',
  payment_method_detail: t?.payment_method_detail ?? null,
  account_id: t?.account_id ?? '',
  card_id: t?.card_id ?? '',
  status: t?.status ?? 'pago',
  notes: t?.notes ?? '',
  recurrence: t?.recurrence ?? 'none',
  installments: t?.installments ?? 1,
  installment_number: t?.installment_number ?? 1,
  installment_group_id: t?.installment_group_id ?? null,
  show_in_calendar: t?.show_in_calendar ?? true,
  payee_id: t?.payee_id ?? null,
  payee_name: t?.payee_name ?? null,
});

const adjustDateToDueDay = (baseDate: string, dueDay: number): string => {
  const d = new Date(baseDate + 'T00:00:00');
  const maxDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(dueDay, maxDay));
  return d.toISOString().split('T')[0];
};

const addMonths = (dateStr: string, months: number, dueDay: number): string => {
  const d = new Date(dateStr + 'T00:00:00');
  d.setMonth(d.getMonth() + months);
  const maxDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(dueDay, maxDay));
  return d.toISOString().split('T')[0];
};

const addWeeks = (dateStr: string, weeks: number): string => {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + weeks * 7);
  return d.toISOString().split('T')[0];
};

const addYears = (dateStr: string, years: number): string => {
  const d = new Date(dateStr + 'T00:00:00');
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().split('T')[0];
};

const inputCls = 'w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50 bg-white';
const labelCls = 'text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block';

export const TransactionModal = ({ open, transaction, onClose, onSave, onSaveMany }: Props) => {
  const { accounts } = useAccounts();
  const { cards } = useCards();
  const { activePayees, quickCreate, getPayeeSuggestions, getRecentPayeeIds } = usePayees();
  const { suggestCategory, preWarmHistory } = useCategorySuggestion();
  const descSuggestTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [form, setForm] = useState<TransactionPayload>(buildInitialForm(transaction));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [useInstallments, setUseInstallments] = useState(false);

  // Smart auto-fill state
  const [autoFillSource, setAutoFillSource] = useState<AutoFillSource>(null);
  const [autoFillFields, setAutoFillFields] = useState<string[]>([]);
  const [suggestRecurring, setSuggestRecurring] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [recentPayeeIds, setRecentPayeeIds] = useState<string[]>([]);
  const [suggestedCategory, setSuggestedCategory] = useState<string | undefined>(undefined);

  const [cardUsageMode, setCardUsageMode] = useState<'credit' | 'debit'>('credit');
  const [recurrenceDueDay, setRecurrenceDueDay] = useState<number>(new Date().getDate());
  const [recurrenceEndType, setRecurrenceEndType] = useState<RecurrenceEndType>('none');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<string>('');
  const [recurrenceCount, setRecurrenceCount] = useState<number>(12);
  const [recurrenceClassification, setRecurrenceClassification] = useState<RecurrenceClassification>('common');
  const [loanTotalValue, setLoanTotalValue] = useState<string>('');
  const [loanInterestRate, setLoanInterestRate] = useState<string>('');
  const [financingDownPayment, setFinancingDownPayment] = useState<string>('');

  // Load recent payees on open
  const loadRecentPayees = useCallback(async () => {
    const ids = await getRecentPayeeIds();
    setRecentPayeeIds(ids);
  }, [getRecentPayeeIds]);

  useEffect(() => {
    if (open) {
      const initial = buildInitialForm(transaction);
      setForm(initial);
      setErrors({});
      setFeedback(null);
      setShowAdvanced(false);
      setUseInstallments((transaction?.installments ?? 1) > 1);
      const day = transaction?.date ? new Date(transaction.date + 'T00:00:00').getDate() : new Date().getDate();
      setRecurrenceDueDay(day);
      setRecurrenceEndType('none');
      setRecurrenceEndDate('');
      setRecurrenceCount(12);
      setRecurrenceClassification('common');
      setLoanTotalValue('');
      setLoanInterestRate('');
      setFinancingDownPayment('');
      setCardUsageMode('credit');
      setAutoFillSource(null);
      setAutoFillFields([]);
      setSuggestRecurring(false);
      setSuggestedCategory(undefined);
      loadRecentPayees();
      // Pre-warm category suggestion history
      preWarmHistory();
    }
  }, [open, transaction, loadRecentPayees, preWarmHistory]);

  useEffect(() => {
    if (open && !form.account_id && accounts.length > 0 && form.payment_method === 'account') {
      setForm((p) => ({ ...p, account_id: accounts[0].id }));
    }
  }, [accounts, open]);

  useEffect(() => {
    if (!form.card_id) return;
    const c = cards.find(card => card.id === form.card_id);
    if (!c) return;
    if (c.card_type === 'debit') setCardUsageMode('debit');
    else setCardUsageMode('credit');
  }, [form.card_id, cards]);

  useEffect(() => {
    setForm((p) => {
      const validReceita = ['recebido', 'pendente', 'atrasado'];
      const validDespesa = ['pago', 'pendente', 'vencido'];
      if (p.type === 'receita' && !validReceita.includes(p.status)) return { ...p, status: 'recebido' };
      if (p.type === 'despesa' && !validDespesa.includes(p.status)) return { ...p, status: 'pago' };
      return p;
    });
  }, [form.type]);

  // ── Description-based category suggestion ──────────────────────────────────
  // Fires when user types a description without a payee selected and no category yet
  useEffect(() => {
    if (!open) return;
    if (form.payee_id) return;               // payee already suggests category
    if (form.category) return;               // category already set

    if (descSuggestTimerRef.current) clearTimeout(descSuggestTimerRef.current);

    if (!form.description || form.description.trim().length < 3) {
      setSuggestedCategory(undefined);
      return;
    }

    descSuggestTimerRef.current = setTimeout(async () => {
      const suggestion = await suggestCategory(form.description, form.type);
      if (suggestion) {
        setSuggestedCategory(suggestion);
        setForm((p) => (p.category ? p : { ...p, category: suggestion }));
      }
    }, 500);

    return () => {
      if (descSuggestTimerRef.current) clearTimeout(descSuggestTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.description, form.type, form.payee_id, open]);

  const statuses = form.type === 'receita' ? RECEIPT_STATUSES : EXPENSE_STATUSES;
  const isRecurring = form.recurrence !== 'none';

  const selectedCard = form.payment_method === 'card' && form.card_id
    ? cards.find(c => c.id === form.card_id) ?? null
    : null;

  const isDebitMode = selectedCard !== null && (
    selectedCard.card_type === 'debit' ||
    (selectedCard.card_type === 'multiple' && cardUsageMode === 'debit')
  );

  const debitAccount = isDebitMode && selectedCard?.linked_account_id
    ? accounts.find(a => a.id === selectedCard.linked_account_id) ?? null
    : null;

  const installmentValue =
    useInstallments && form.installments > 1 && Number(form.amount) > 0 && !isDebitMode && form.payment_method === 'card'
      ? Number(form.amount) / form.installments
      : null;

  const estimatedTotal = isRecurring && recurrenceEndType === 'count' && Number(form.amount) > 0
    ? Number(form.amount) * recurrenceCount
    : null;

  // ── Smart payee handler ──────────────────────────────────────────────────
  const handlePayeeChange = useCallback(async (
    id: string | null,
    name: string | null,
    category?: string | null,
    subcategory?: string | null
  ) => {
    // Reset auto-fill hints
    setAutoFillSource(null);
    setAutoFillFields([]);
    setSuggestRecurring(false);
    setSuggestedCategory(undefined);

    if (!id) {
      setForm(p => ({ ...p, payee_id: null, payee_name: null }));
      return;
    }

    // Immediately set payee and apply category defaults from payee profile
    const filled: string[] = [];
    const updates: Partial<TransactionPayload> = { payee_id: id, payee_name: name };

    // Auto-fill description with payee name (if blank)
    if (!form.description.trim()) {
      updates.description = name ?? '';
      filled.push('descrição');
    }

    // Apply category default from payee profile
    if (category) {
      updates.category = category;
      updates.subcategory = subcategory ?? '';
      setSuggestedCategory(category);
      filled.push('categoria');
    }

    setForm(p => ({ ...p, ...updates }));
    if (filled.length > 0) setAutoFillSource('payee');
    setAutoFillFields(prev => [...new Set([...prev, ...filled])]);

    // Now fetch history-based suggestions
    setLoadingSuggestions(true);
    try {
      const suggestions = await getPayeeSuggestions(id);
      if (suggestions) {
        const histFilled: string[] = [];
        const histUpdates: Partial<TransactionPayload> = {};

        // Category from history if not already set by payee profile
        if (!category && suggestions.category) {
          histUpdates.category = suggestions.category;
          histUpdates.subcategory = suggestions.subcategory ?? '';
          setSuggestedCategory(suggestions.category);
          histFilled.push('categoria');
        }

        // Payment method from history
        if (suggestions.payment_method) {
          histUpdates.payment_method = suggestions.payment_method;
          histFilled.push('forma de pagamento');
        }

        // Payment method detail from history
        if (suggestions.payment_method_detail) {
          histUpdates.payment_method_detail = suggestions.payment_method_detail;
        }

        // Account from history (only if account mode)
        if (suggestions.payment_method !== 'card' && suggestions.account_id) {
          const accExists = accounts.find(a => a.id === suggestions.account_id);
          if (accExists) {
            histUpdates.account_id = suggestions.account_id;
            histFilled.push('conta');
          }
        }

        // Card from history (only if card mode)
        if (suggestions.payment_method === 'card' && suggestions.card_id) {
          const cardExists = cards.find(c => c.id === suggestions.card_id);
          if (cardExists) {
            histUpdates.card_id = suggestions.card_id;
            histFilled.push('cartão');
          }
        }

        if (Object.keys(histUpdates).length > 0) {
          setForm(p => ({ ...p, ...histUpdates }));
        }

        if (histFilled.length > 0) {
          setAutoFillSource('history');
          setAutoFillFields(prev => [...new Set([...prev, ...histFilled])]);
        }

        if (suggestions.suggestsRecurring) {
          setSuggestRecurring(true);
        }
      }
    } finally {
      setLoadingSuggestions(false);
    }
  }, [form.description, getPayeeSuggestions, accounts, cards]);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.description.trim()) errs.description = 'Descrição é obrigatória.';
    if (!form.amount || Number(form.amount) <= 0) errs.amount = 'Informe um valor maior que zero.';
    if (!form.date) errs.date = 'Data é obrigatória.';
    if (isRecurring && recurrenceEndType === 'date' && !recurrenceEndDate) errs.recurrenceEndDate = 'Informe a data final.';
    if (isRecurring && recurrenceEndType === 'count' && (!recurrenceCount || recurrenceCount < 1)) errs.recurrenceCount = 'Informe o número de repetições.';
    if (isDebitMode && !selectedCard?.linked_account_id) {
      errs.card_id = 'Este cartão de débito não possui conta vinculada. Edite o cartão e vincule uma conta.';
    }
    if (
      form.type === 'despesa' &&
      form.payment_method === 'account' &&
      !!form.account_id &&
      form.status === 'pago' &&
      !form.payment_method_detail
    ) {
      errs.payment_method_detail = 'Informe como esta despesa foi paga (Pix, Boleto, etc.).';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const buildBasePayload = (): Partial<TransactionPayload> => {
    if (isDebitMode && selectedCard) {
      return { payment_method: 'account', account_id: selectedCard.linked_account_id ?? undefined, card_id: selectedCard.id, installments: 1 };
    }
    return {
      payment_method: form.payment_method,
      account_id: form.payment_method === 'account' ? (form.account_id || undefined) : undefined,
      card_id: form.payment_method === 'card' ? (form.card_id || undefined) : undefined,
    };
  };

  const buildRecurringRecords = (): TransactionPayload[] => {
    const baseDate = adjustDateToDueDay(form.date, recurrenceDueDay);
    const classificationLabel = recurrenceClassification !== 'common'
      ? RECURRENCE_TYPES.find(r => r.value === recurrenceClassification)?.label ?? ''
      : '';
    const basePayload: TransactionPayload = {
      ...form,
      amount: Number(form.amount),
      account_id: form.payment_method === 'account' ? (form.account_id || null) : null,
      card_id: form.payment_method === 'card' ? (form.card_id || null) : null,
      installments: 1,
      installment_number: 1,
      installment_group_id: null,
      notes: [form.notes, classificationLabel].filter(Boolean).join(' | '),
    };
    if (recurrenceEndType === 'none') return [{ ...basePayload, date: baseDate }];

    const records: TransactionPayload[] = [];
    let count = 0;
    if (recurrenceEndType === 'count') {
      count = recurrenceCount;
    } else if (recurrenceEndType === 'date' && recurrenceEndDate) {
      let tmpDate = baseDate;
      let safety = 0;
      while (tmpDate <= recurrenceEndDate && safety < 600) {
        count++;
        safety++;
        if (form.recurrence === 'weekly') tmpDate = addWeeks(tmpDate, 1);
        else if (form.recurrence === 'monthly') tmpDate = addMonths(tmpDate, 1, recurrenceDueDay);
        else if (form.recurrence === 'annual') tmpDate = addYears(tmpDate, 1);
        else break;
      }
    }

    let currentDate = baseDate;
    for (let i = 0; i < count; i++) {
      records.push({ ...basePayload, date: currentDate, description: form.description, status: i === 0 ? form.status : 'pendente' });
      if (form.recurrence === 'weekly') currentDate = addWeeks(currentDate, 1);
      else if (form.recurrence === 'monthly') currentDate = addMonths(currentDate, 1, recurrenceDueDay);
      else if (form.recurrence === 'annual') currentDate = addYears(currentDate, 1);
    }
    return records;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setFeedback(null);

    const baseOverride = buildBasePayload();
    let result: { success: boolean; message: string };

    if (isRecurring && recurrenceEndType !== 'none' && !transaction) {
      const records = buildRecurringRecords();
      const finalRecords = records.map(r => ({ ...r, ...baseOverride, installments: 1, installment_number: 1, installment_group_id: null }));
      if (finalRecords.length === 1) {
        result = await onSave(finalRecords[0]);
      } else if (onSaveMany) {
        result = await onSaveMany(finalRecords);
      } else {
        result = await onSave({ ...finalRecords[0], description: form.description, installments: 1, installment_group_id: null });
      }
    } else if (isRecurring && recurrenceEndType === 'none' && !transaction) {
      const baseDate = adjustDateToDueDay(form.date, recurrenceDueDay);
      const classificationLabel = recurrenceClassification !== 'common' ? RECURRENCE_TYPES.find(r => r.value === recurrenceClassification)?.label ?? '' : '';
      result = await onSave({ ...form, ...baseOverride, date: baseDate, amount: Number(form.amount), installments: 1, notes: [form.notes, classificationLabel].filter(Boolean).join(' | ') });
    } else if (useInstallments && form.installments > 1 && !transaction && !isDebitMode && form.payment_method === 'card') {
      result = await onSave({ ...form, ...baseOverride, amount: Number(form.amount), installments: form.installments });
    } else {
      result = await onSave({ ...form, ...baseOverride, amount: Number(form.amount), installments: isDebitMode ? 1 : (useInstallments && form.payment_method === 'card' ? form.installments : 1) });
    }

    setSaving(false);
    if (!result.success) setFeedback(result.message);
    else {
      if (!transaction) {
        trackEvent('transaction_created', {
          type: form.type,
          category: form.category || undefined,
          has_payee: !!form.payee_id,
          payment_method: form.payment_method,
          is_recurring: isRecurring,
        });
      }
      onClose();
    }
  };

  const advancedBadgeCount = [
    form.recurrence !== 'none',
    useInstallments && !isDebitMode && form.payment_method === 'card',
    !form.show_in_calendar,
  ].filter(Boolean).length;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl max-w-lg w-full mx-4 max-h-[94vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div>
            <h3 className="text-base font-700 text-gray-900">
              {transaction ? 'Editar Lançamento' : 'Novo Lançamento'}
            </h3>
            {!transaction && (
              <p className="text-[11px] text-gray-400 mt-0.5">Selecione o favorecido para preencher automaticamente</p>
            )}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
            <i className="ri-close-line" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {feedback && (
            <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-3 py-2.5 rounded-xl">
              <i className="ri-error-warning-line shrink-0" />{feedback}
            </div>
          )}

          {/* ── 1. TIPO ── */}
          <div>
            <label className={labelCls}>Tipo</label>
            <div className="grid grid-cols-2 gap-2">
              {(['despesa', 'receita'] as const).map((t) => (
                <button key={t} type="button"
                  onClick={() => setForm((p) => ({ ...p, type: t, category: '', subcategory: '' }))}
                  className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border font-600 text-sm transition-all cursor-pointer whitespace-nowrap ${
                    form.type === t
                      ? t === 'despesa' ? 'border-red-400 bg-red-50 text-red-700' : 'border-emerald-400 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                >
                  <i className={t === 'despesa' ? 'ri-arrow-down-line' : 'ri-arrow-up-line'} />
                  {t === 'despesa' ? 'Despesa' : 'Receita'}
                </button>
              ))}
            </div>
          </div>

          {/* ── 2. FAVORECIDO (motor) ── */}
          <div>
            <label className={labelCls}>
              Favorecido
              <span className="normal-case font-400 text-gray-400 ml-1">(opcional)</span>
              {loadingSuggestions && <i className="ri-loader-4-line animate-spin ml-2 text-emerald-500" />}
            </label>
            <PayeeSelector
              payees={activePayees}
              value={form.payee_id ?? null}
              valueName={form.payee_name ?? null}
              recentPayeeIds={recentPayeeIds}
              onChange={handlePayeeChange}
              onQuickCreate={(name, cat, sub) => quickCreate(name, cat, sub)}
            />

            {/* Auto-fill hint */}
            {autoFillSource && autoFillFields.length > 0 && (
              <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-emerald-600">
                <i className="ri-magic-line shrink-0" />
                <span>
                  Preenchido automaticamente com base no {autoFillSource === 'history' ? 'histórico' : 'perfil do favorecido'}:
                  {' '}<strong>{autoFillFields.join(', ')}</strong>
                </span>
              </div>
            )}

            {/* Recurrence suggestion */}
            {suggestRecurring && form.recurrence === 'none' && (
              <div className="mt-2 flex items-center justify-between gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-center gap-2 min-w-0">
                  <i className="ri-repeat-line text-amber-500 shrink-0 text-sm" />
                  <p className="text-xs text-amber-700 truncate">Parece recorrente. Repetir mensalmente?</p>
                </div>
                <button
                  type="button"
                  onClick={() => { setForm(p => ({ ...p, recurrence: 'monthly' })); setShowAdvanced(true); setSuggestRecurring(false); }}
                  className="text-xs font-700 text-amber-700 hover:text-amber-800 cursor-pointer whitespace-nowrap shrink-0"
                >
                  Sim
                </button>
              </div>
            )}
          </div>

          {/* ── 3+4. VALOR + DATA ── */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Valor (R$)</label>
              <CurrencyInput
                value={Number(form.amount) || 0}
                onChange={(v) => setForm((p) => ({ ...p, amount: v as unknown as number }))}
                placeholder="0,00"
                className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50 ${errors.amount ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
              />
              {errors.amount && <p className="text-xs text-red-500 mt-1">{errors.amount}</p>}
            </div>
            <div>
              <label className={labelCls}>Vencimento / Competência</label>
              <input type="date" value={form.date}
                onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50 cursor-pointer"
              />
              {errors.date && <p className="text-xs text-red-500 mt-1">{errors.date}</p>}
            </div>
          </div>

          {/* ── 5. DESCRIÇÃO ── */}
          <div>
            <label className={labelCls}>Descrição</label>
            <input value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="Ex: Almoço, Salário, Netflix..."
              className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50 ${errors.description ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
            />
            {errors.description && <p className="text-xs text-red-500 mt-1">{errors.description}</p>}
          </div>

          {/* ── 6. CATEGORIA ── */}
          <div>
            <label className={labelCls}>
              Categoria
              {suggestedCategory && (
                <span className="normal-case font-400 text-emerald-500 ml-1 text-[10px]">· sugerida</span>
              )}
            </label>
            <CategoryPicker
              transactionType={form.type}
              selected={form.category}
              onSelect={(name) => { setForm((p) => ({ ...p, category: name })); setSuggestedCategory(undefined); }}
              subcategory={form.subcategory ?? ''}
              onSubcategoryChange={(val) => setForm((p) => ({ ...p, subcategory: val }))}
              suggested={suggestedCategory}
            />
          </div>

          {/* ── 7. FORMA DE PAGAMENTO ── */}
          <div>
            <label className={labelCls}>Forma de pagamento</label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              {([
                ['account', 'ri-bank-line', 'Conta bancária'],
                ['card', 'ri-bank-card-line', 'Cartão'],
              ] as const).map(([pm, icon, label]) => (
                <button key={pm} type="button"
                  onClick={() => {
                    if (pm === 'account') {
                      setUseInstallments(false);
                      setForm((p) => ({ ...p, payment_method: pm, installments: 1 }));
                    } else {
                      setForm((p) => ({ ...p, payment_method: pm, payment_method_detail: null }));
                    }
                  }}
                  className={`flex items-center gap-2 py-2.5 px-3 rounded-xl border font-600 text-sm transition-all cursor-pointer whitespace-nowrap ${
                    form.payment_method === pm
                      ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                >
                  <i className={`${icon} text-sm`} />{label}
                </button>
              ))}
            </div>

            {/* ── 8. CONTA / CARTÃO ── */}
            {form.payment_method === 'account' ? (
              <select value={form.account_id ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, account_id: e.target.value, payment_method_detail: e.target.value ? p.payment_method_detail : null }))}
                className={inputCls}>
                <option value="">Sem conta vinculada</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            ) : (
              <>
                <select value={form.card_id ?? ''}
                  onChange={(e) => setForm((p) => ({ ...p, card_id: e.target.value }))}
                  className={`${inputCls} ${errors.card_id ? 'border-red-300' : ''}`}>
                  <option value="">Sem cartão vinculado</option>
                  {cards.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.card_type === 'debit' ? ' (Débito)' : c.card_type === 'multiple' ? ' (Múltiplo)' : ''}
                    </option>
                  ))}
                </select>
                {errors.card_id && <p className="text-xs text-red-500 mt-1">{errors.card_id}</p>}

                {selectedCard && (
                  <div className="mt-2 space-y-2">
                    {selectedCard.card_type === 'multiple' && (
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-100">
                        <i className="ri-swap-line text-gray-500 text-sm shrink-0" />
                        <span className="text-xs text-gray-600 font-600 mr-auto">Usar como:</span>
                        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                          <button type="button" onClick={() => setCardUsageMode('credit')}
                            className={`px-3 py-1.5 text-xs font-600 cursor-pointer whitespace-nowrap transition-colors ${cardUsageMode === 'credit' ? 'bg-emerald-500 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                            <i className="ri-bank-card-2-line mr-1" />Crédito
                          </button>
                          <button type="button" onClick={() => setCardUsageMode('debit')}
                            className={`px-3 py-1.5 text-xs font-600 cursor-pointer whitespace-nowrap transition-colors border-l border-gray-200 ${cardUsageMode === 'debit' ? 'bg-sky-500 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                            <i className="ri-bank-card-line mr-1" />Débito
                          </button>
                        </div>
                      </div>
                    )}
                    {isDebitMode ? (
                      <div className="flex items-start gap-2 px-3 py-2.5 bg-sky-50 border border-sky-100 rounded-xl">
                        <i className="ri-bank-card-line text-sky-500 text-sm shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-600 text-sky-700">Débito direto na conta</p>
                          <p className="text-xs text-sky-600 mt-0.5">
                            {debitAccount ? <>Valor debitado de <strong>{debitAccount.name}</strong>. Sem fatura.</> : 'Valor debitado na conta vinculada.'}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2 px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-xl">
                        <i className="ri-bank-card-2-line text-emerald-500 text-sm shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-600 text-emerald-700">Crédito — lançará na fatura</p>
                          <p className="text-xs text-emerald-600 mt-0.5">Impacta o limite. Parcelamento disponível em Opções avançadas.</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── 9. STATUS ── */}
          <div>
            <label className={labelCls}>Status</label>
            <div className="grid grid-cols-3 gap-2">
              {statuses.map((s) => (
                <button key={s.value} type="button"
                  onClick={() => {
                    const isPaidStatus = s.value === 'pago' || s.value === 'recebido';
                    const today = new Date().toISOString().split('T')[0];
                    setForm((p) => ({
                      ...p,
                      status: s.value,
                      paid_at: isPaidStatus
                        ? (p.paid_at ?? today)
                        : null,
                    }));
                  }}
                  className={`py-2 rounded-xl border text-xs font-600 transition-all cursor-pointer whitespace-nowrap ${form.status === s.value ? s.activeClass : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                  {s.label}
                </button>
              ))}
            </div>

            {/* Data real de pagamento — aparece quando pago/recebido */}
            {(form.status === 'pago' || form.status === 'recebido') && (
              <div className="mt-2.5 flex items-center gap-2.5 px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-xl">
                <i className="ri-checkbox-circle-line text-emerald-500 text-base shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-600 text-emerald-700 mb-1">
                    {form.status === 'recebido' ? 'Recebido em' : 'Pago em'}
                  </p>
                  <input
                    type="date"
                    value={form.paid_at ?? new Date().toISOString().split('T')[0]}
                    onChange={(e) => setForm((p) => ({ ...p, paid_at: e.target.value }))}
                    className="w-full px-2.5 py-1.5 border border-emerald-200 rounded-lg text-xs bg-white focus:outline-none focus:border-emerald-400 cursor-pointer"
                  />
                  <p className="text-[10px] text-emerald-600 mt-1">
                    O vencimento ({new Date(form.date + 'T00:00:00').toLocaleDateString('pt-BR')}) permanece inalterado
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Como foi pago? — despesa via conta */}
          {form.payment_method === 'account' && form.type === 'despesa' && !!form.account_id && (
            <div>
              <p className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                Como foi pago?
                {form.status === 'pago'
                  ? <span className="normal-case font-500 text-red-400 text-[11px]">obrigatório</span>
                  : <span className="normal-case font-400 text-gray-400 text-[11px]">opcional se pendente</span>
                }
              </p>
              <div className="grid grid-cols-4 gap-1.5">
                {PAYMENT_METHODS.map(pm => (
                  <button key={pm.value} type="button"
                    onClick={() => setForm(p => ({ ...p, payment_method_detail: p.payment_method_detail === pm.value ? null : pm.value }))}
                    className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border text-[10px] font-600 transition-all cursor-pointer ${
                      form.payment_method_detail === pm.value
                        ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                        : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}>
                    <i className={`${pm.icon} text-base ${form.payment_method_detail === pm.value ? 'text-emerald-500' : pm.color}`} />
                    {pm.label}
                  </button>
                ))}
              </div>
              {errors.payment_method_detail && <p className="text-xs text-red-500 mt-1.5">{errors.payment_method_detail}</p>}
            </div>
          )}

          {/* ── 10. OBSERVAÇÃO ── */}
          <div>
            <label className={labelCls}>Observação <span className="normal-case font-400 text-gray-400">(opcional)</span></label>
            <textarea value={form.notes}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              rows={2} maxLength={500}
              placeholder="Observações adicionais..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 resize-none"
            />
          </div>

          {/* ── 11. OPÇÕES AVANÇADAS ── */}
          <div>
            <button type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className="flex items-center gap-2 text-sm font-600 text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap"
            >
              <i className={`text-sm transition-transform ${showAdvanced ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
              Opções avançadas
              {advancedBadgeCount > 0 && (
                <span className="w-4 h-4 flex items-center justify-center bg-emerald-500 text-white rounded-full text-[10px]">
                  {advancedBadgeCount}
                </span>
              )}
            </button>

            {showAdvanced && (
              <div className="mt-3 space-y-5 p-4 bg-gray-50 rounded-2xl border border-gray-100">

                {/* Calendar toggle — moved to advanced */}
                <div className="flex items-center justify-between py-2 px-3 bg-white rounded-xl border border-gray-100">
                  <label className="flex items-center gap-2 text-sm font-600 text-gray-600 cursor-pointer select-none">
                    <i className="ri-calendar-event-line text-emerald-500 text-base" />
                    Marcar no calendário
                  </label>
                  <button type="button"
                    onClick={() => setForm((p) => ({ ...p, show_in_calendar: !p.show_in_calendar }))}
                    className={`relative w-9 h-5 rounded-full transition-all cursor-pointer shrink-0 ${form.show_in_calendar ? 'bg-emerald-500' : 'bg-gray-200'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${form.show_in_calendar ? 'left-4' : 'left-0.5'}`} />
                  </button>
                </div>

                {/* ── Recorrência ── */}
                <div>
                  <label className={labelCls + ' flex items-center gap-1.5'}>
                    <i className="ri-repeat-line text-emerald-600" />Recorrência
                  </label>
                  <div className="grid grid-cols-2 gap-1.5 mb-3">
                    {RECURRENCES.map((r) => (
                      <button key={r.value} type="button"
                        onClick={() => setForm((p) => ({ ...p, recurrence: r.value }))}
                        className={`flex items-center gap-2 py-2 px-3 rounded-xl border text-xs font-600 text-left transition-all cursor-pointer ${
                          form.recurrence === r.value
                            ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                            : 'border-gray-200 text-gray-500 bg-white hover:border-gray-300'}`}
                      >
                        <i className={`${r.icon} text-sm`} />{r.label}
                      </button>
                    ))}
                  </div>

                  {isRecurring && (
                    <div className="space-y-3 p-3 bg-white rounded-xl border border-emerald-100">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Dia de vencimento</label>
                          <select value={recurrenceDueDay} onChange={(e) => setRecurrenceDueDay(Number(e.target.value))} className={inputCls}>
                            {DUE_DAYS.map((d) => <option key={d} value={d}>Dia {d}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Classificação</label>
                          <select value={recurrenceClassification} onChange={(e) => setRecurrenceClassification(e.target.value as RecurrenceClassification)} className={inputCls}>
                            {RECURRENCE_TYPES.map((rt) => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
                          </select>
                          <p className="text-[11px] text-gray-400 mt-1">{RECURRENCE_TYPES.find(r => r.value === recurrenceClassification)?.desc}</p>
                        </div>
                      </div>

                      {(recurrenceClassification === 'loan' || recurrenceClassification === 'financing') && !transaction && (
                        <div className="grid grid-cols-2 gap-3 pt-1 border-t border-gray-100">
                          <div>
                            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">
                              {recurrenceClassification === 'financing' ? 'Valor do bem' : 'Valor total do empréstimo'}
                            </label>
                            <input type="number" min={0} step="0.01" value={loanTotalValue} onChange={(e) => setLoanTotalValue(e.target.value)} placeholder="R$ 0,00" className={inputCls} />
                          </div>
                          {recurrenceClassification === 'financing' ? (
                            <div>
                              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Entrada (R$)</label>
                              <input type="number" min={0} step="0.01" value={financingDownPayment} onChange={(e) => setFinancingDownPayment(e.target.value)} placeholder="R$ 0,00" className={inputCls} />
                            </div>
                          ) : (
                            <div>
                              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Taxa de juros (%a.m.)</label>
                              <input type="number" min={0} step="0.01" value={loanInterestRate} onChange={(e) => setLoanInterestRate(e.target.value)} placeholder="Ex: 2.5" className={inputCls} />
                            </div>
                          )}
                        </div>
                      )}

                      {!transaction && (
                        <>
                          <div className="border-t border-gray-100 pt-3">
                            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-2 block">Duração da recorrência</label>
                            <div className="grid grid-cols-3 gap-1.5">
                              {([
                                ['none', 'ri-infinity-line', 'Contínuo'],
                                ['count', 'ri-hashtag', 'Qtd. de vezes'],
                                ['date', 'ri-calendar-check-line', 'Até data'],
                              ] as const).map(([val, icon, lbl]) => (
                                <button key={val} type="button"
                                  onClick={() => setRecurrenceEndType(val as RecurrenceEndType)}
                                  className={`flex flex-col items-center gap-1 py-2 px-2 rounded-xl border text-[11px] font-600 transition-all cursor-pointer ${
                                    recurrenceEndType === val
                                      ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                                      : 'border-gray-200 text-gray-500 bg-white hover:border-gray-300'}`}
                                >
                                  <i className={`${icon} text-base`} />{lbl}
                                </button>
                              ))}
                            </div>
                          </div>

                          {recurrenceEndType === 'count' && (
                            <div>
                              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Quantidade de repetições</label>
                              <div className="flex items-center gap-3">
                                <input type="number" min={1} max={600} value={recurrenceCount} onChange={(e) => setRecurrenceCount(parseInt(e.target.value) || 1)} className={inputCls + ' max-w-28'} />
                                <span className="text-xs text-gray-500">vezes</span>
                              </div>
                              {errors.recurrenceCount && <p className="text-xs text-red-500 mt-1">{errors.recurrenceCount}</p>}
                              {estimatedTotal !== null && (
                                <div className="mt-2 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-xl">
                                  <p className="text-xs text-emerald-700 font-600">
                                    <i className="ri-calculator-line mr-1" />
                                    {recurrenceCount}x de {Number(form.amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} = total {estimatedTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}

                          {recurrenceEndType === 'date' && (
                            <div>
                              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Repetir até</label>
                              <input type="date" value={recurrenceEndDate} onChange={(e) => setRecurrenceEndDate(e.target.value)} min={form.date} className={inputCls + ' cursor-pointer'} />
                              {errors.recurrenceEndDate && <p className="text-xs text-red-500 mt-1">{errors.recurrenceEndDate}</p>}
                            </div>
                          )}

                          <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-xl">
                            <i className="ri-information-line text-amber-500 text-sm mt-0.5 shrink-0" />
                            <p className="text-xs text-amber-700 leading-relaxed">
                              {recurrenceEndType === 'none'
                                ? `Será criado 1 lançamento com recorrência ${RECURRENCES.find(r => r.value === form.recurrence)?.label?.toLowerCase()}.`
                                : recurrenceEndType === 'count'
                                ? `Serão criados ${recurrenceCount} lançamentos ${RECURRENCES.find(r => r.value === form.recurrence)?.label?.toLowerCase() ?? ''}s, dia ${recurrenceDueDay} de cada período.`
                                : `Serão criados lançamentos até ${recurrenceEndDate ? new Date(recurrenceEndDate + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}, dia ${recurrenceDueDay} de cada período.`
                              }
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* ── Parcelamento ── */}
                {form.type === 'despesa' && !transaction && form.payment_method === 'card' && !isDebitMode && (
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className={labelCls + ' mb-0 flex items-center gap-1.5'}>
                        <i className="ri-bill-line text-emerald-600" />Parcelamento
                        <span className="text-[10px] font-500 text-gray-400 normal-case">(somente crédito)</span>
                      </label>
                      <button type="button"
                        onClick={() => { setUseInstallments((v) => !v); setForm((p) => ({ ...p, installments: 2 })); }}
                        className={`relative w-9 h-5 rounded-full transition-all cursor-pointer ${useInstallments ? 'bg-emerald-500' : 'bg-gray-200'}`}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${useInstallments ? 'left-4' : 'left-0.5'}`} />
                      </button>
                    </div>
                    {useInstallments && (
                      <div className="space-y-3 p-3 bg-white rounded-xl border border-gray-100">
                        <div className="flex items-start gap-3">
                          <div className="flex-1">
                            <label className="text-xs text-gray-500 mb-1 block">Nº de parcelas</label>
                            <input type="number" min={2} max={48} value={form.installments} onChange={(e) => setForm((p) => ({ ...p, installments: parseInt(e.target.value) || 2 }))} className={inputCls} />
                          </div>
                          {installmentValue !== null && (
                            <div className="flex-1">
                              <label className="text-xs text-gray-500 mb-1 block">Valor por parcela</label>
                              <div className="px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-xl text-sm font-700 text-emerald-700">
                                {installmentValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                              </div>
                            </div>
                          )}
                        </div>
                        {installmentValue !== null && (
                          <p className="text-xs text-gray-500 bg-amber-50 border border-amber-100 px-3 py-2 rounded-xl">
                            <i className="ri-information-line mr-1 text-amber-500" />
                            Serão criados <strong>{form.installments}</strong> lançamentos mensais de{' '}
                            <strong>{installmentValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong> cada.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {form.type === 'despesa' && !transaction && form.payment_method === 'card' && isDebitMode && (
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 border border-gray-100 rounded-xl">
                      <i className="ri-lock-line text-gray-400 text-sm" />
                      <div>
                        <p className="text-xs font-600 text-gray-500">Parcelamento indisponível no débito</p>
                        <p className="text-xs text-gray-400">Cartões de débito debitam o valor integral imediatamente.</p>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2 pb-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-600 rounded-xl text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              Cancelar
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
              {saving && <i className="ri-loader-4-line animate-spin" />}
              {saving ? 'Salvando...'
                : isRecurring && recurrenceEndType === 'count' && !transaction ? `Criar ${recurrenceCount}x recorrências`
                : isRecurring && !transaction ? 'Criar lançamento recorrente'
                : useInstallments && !transaction && !isDebitMode && form.payment_method === 'card' ? `Criar ${form.installments}x parcelas`
                : transaction ? 'Salvar' : 'Criar lançamento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
